import { useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Sparkles } from "lucide-react";
import { parseAiSuggestions } from "../../utils/parseAiSuggestions";
import { SuggestionIssueCard, SuggestionsSummary } from "./SuggestionCards";
import { Badge } from "../ui/Badge";
import AiModelModal from "../modals/AiModelModal";
import { DEFAULT_AI_MODEL, getAiModelLabel } from "../../constants/aiModels";

const Suggestions = ({
  metrics,
  currentAiModel,
  knownIssues,
  setAiModel,
  onOpenSocialTab,
}) => {
  const [modelModalOpen, setModelModalOpen] = useState(false);

  if (!metrics || !metrics.suggestions) {
    return (
      <div
        className="p-4 sm:p-6 bg-card rounded-xl border border-border shadow-subtle text-center text-muted-foreground"
        aria-live="polite"
      >
        <span role="img" aria-label="No AI summary">
          <Sparkles size={18} className="inline text-brand" aria-hidden />
        </span>{" "}
        No AI summary available yet.
      </div>
    );
  }

  const parsed = parseAiSuggestions(metrics.suggestions);
  const useStructured = parsed.issues.length > 0;

  const sanitizedHtml = DOMPurify.sanitize(marked.parse(metrics.suggestions));

  const testAiModel =
    metrics?.aiModel || metrics?.ai_model || DEFAULT_AI_MODEL;
  const activeAiModel = currentAiModel || testAiModel;
  const showRetest =
    currentAiModel && testAiModel && currentAiModel !== testAiModel;
  const stackLabel = metrics?.siteStack?.name;

  return (
    <section
      className="transition-opacity duration-500 opacity-100"
      aria-labelledby="ai-summary-title"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4 w-full">
        <h2
          id="ai-summary-title"
          className="text-lg font-semibold text-foreground flex items-center gap-2"
        >
          <Sparkles size={18} className="text-brand shrink-0" aria-hidden />
          AI Summary
        </h2>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {setAiModel ? (
            <button
              type="button"
              onClick={() => setModelModalOpen(true)}
              title={`${activeAiModel}. Click to change model.`}
              aria-label="Change AI model"
              className="inline-flex items-center rounded-lg border px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground border-border hover:bg-brand/10 hover:text-brand hover:border-brand/30 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {getAiModelLabel(activeAiModel)}
            </button>
          ) : (
            <Badge title={activeAiModel}>{getAiModelLabel(activeAiModel)}</Badge>
          )}
          {showRetest && (
            <button
              type="button"
              className="px-2 py-0.5 text-xs font-semibold text-warning hover:underline"
              aria-label="Retest required"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Retest!
            </button>
          )}
        </div>
      </div>

      {useStructured ? (
        <div className="space-y-4" aria-live="polite">
          <SuggestionsSummary text={parsed.summary} stackLabel={stackLabel} />
          {parsed.issues.map((issue, i) => (
            <SuggestionIssueCard
              key={`${issue.title}-${i}`}
              issue={issue}
              listIndex={i}
              pageUrl={metrics.url}
              knownIssues={knownIssues}
              screenshot={metrics.fullPageScreenshot}
              strategy={metrics.device}
              onOpenSocialTab={onOpenSocialTab}
            />
          ))}
        </div>
      ) : (
        <div
          className="prose prose-sm sm:prose max-w-none text-foreground rounded-xl border border-border bg-card p-4 sm:p-6"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          aria-live="polite"
        />
      )}

      {setAiModel && (
        <AiModelModal
          isOpen={modelModalOpen}
          onClose={() => setModelModalOpen(false)}
          aiModel={activeAiModel}
          setAiModel={setAiModel}
        />
      )}
    </section>
  );
};

export default Suggestions;
